"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ CSV, written once. C17.                                                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **In `shared` so there is exactly one writer.** T-10's whole point is that nine export menus
 * route into one path; two implementations of the escaping would be two answers to "what happens to
 * a comma in a task title", and the second one is always the one nobody tested.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.csvCell = csvCell;
exports.toCsv = toCsv;
exports.exportFilename = exportFilename;
/**
 * ⚠️⚠️ **A LEADING `=`, `+`, `-`, `@`, TAB OR CR MAKES A SPREADSHEET EVALUATE THE CELL.** A task
 * titled `=HYPERLINK("http://evil","click")` is a live formula the moment somebody opens the export
 * in Excel, Sheets or LibreOffice — CSV injection, and it is a real path from "anybody who can name a
 * task" to "code runs on a finance laptop".
 *
 * The fix is a leading apostrophe, which every spreadsheet reads as "this is text" and strips on
 * display. ⚠️ **Not stripping the character instead**, because the value would then be wrong: a task
 * genuinely called `-1 nap` has to survive the round trip.
 */
const RISKY_PREFIX = /^[=+\-@\t\r]/;
/**
 * One cell, escaped.
 *
 * ⚠️ **Quoting is unconditional when the value contains a comma, a quote, a newline or a carriage
 * return** — and RFC 4180 doubles an embedded quote rather than backslash-escaping it, which is the
 * detail hand-rolled writers get wrong. A `"` written as `\"` produces a file Excel silently reads as
 * one long broken column.
 */
function csvCell(value) {
    if (value === null || value === undefined)
        return '';
    /**
     * ⚠️⚠️ **A `timestamptz` COLUMN ARRIVES AS A `Date`, AND `String(date)` IS A LOCALE FORMAT.**
     * `deleted_at` and `archived_at` both reach this function that way, and `String()` would write
     * *"Mon Aug 25 2026 00:00:00 GMT+0200 (Central European Summer Time)"* into a cell — a value that
     * differs by the server's locale and that no spreadsheet parses back. ISO, like every other date in
     * this product. Lint found it, by refusing `String(unknown)`.
     *
     * ⚠️ **Every branch is explicit rather than falling through to `String`.** `String(aSymbol)` throws
     * and a function stringifies to its source, so an exhaustive check is the difference between a
     * wrong cell and a 500 from an export somebody was waiting on. Anything unaccounted for goes in as
     * JSON — visible and wrong, rather than `[object Object]`, which is invisible and wrong.
     */
    const text = textOf(value);
    const guarded = RISKY_PREFIX.test(text) ? `'${text}` : text;
    return /[",\r\n]/.test(guarded) ? `"${guarded.replaceAll('"', '""')}"` : guarded;
}
/**
 * A whole file.
 *
 * ⚠️⚠️ **CRLF, AND A BOM.** Both are for Excel and both are load-bearing in Hungarian: without the
 * byte-order mark Excel on Windows reads the file as the system codepage, so every `ő` and `ű` in a
 * project name arrives as mojibake — which reads as a corrupted export rather than as a settings
 * problem. RFC 4180 specifies CRLF; Excel is the reason to actually honour it.
 *
 * ⚠️ The BOM is the caller's to include or not — `toCsv` returns it, and the HTTP path serves it as
 * bytes. A consumer parsing this programmatically strips U+FEFF, which every CSV library does.
 *
 * ⚠️ Written as the escape U+FEFF rather than as the character: lint reads a literal BOM as
 * irregular whitespace, and it is invisible in a diff — a poor property for a byte that decides how
 * the whole file is decoded.
 */
function toCsv(columns, rows, options = {}) {
    const lines = [columns.map(csvCell).join(',')];
    for (const row of rows)
        lines.push(row.map(csvCell).join(','));
    const body = lines.join('\r\n') + '\r\n';
    /* ⚠️ The escape, spelled out — see the note above. */
    return options.bom === false ? body : '\uFEFF' + body;
}
/**
 * A filename a browser and a mail client will both accept.
 *
 * ⚠️ **ASCII only, and dated.** A Hungarian workspace name in a `Content-Disposition` header needs
 * RFC 5987 encoding that not every client implements, and an export somebody downloads three times in
 * a week needs three distinguishable names.
 *
 * ⚠️ **The extension is a parameter since C48, and the caller that needed it is not an export**: the
 * Agent's thread can save a reply as `.md`. It comes through here rather than composing its own name
 * because `forecastify-<what>-<date>` is the shape everything this product hands to a browser has —
 * one convention, so a Downloads folder sorts by product and then by day. ⚠️ Two saves on the same
 * day collide and the browser appends its own `(1)`, which is the behaviour the dating already
 * accepted above.
 */
function exportFilename(domain, on, extension = 'csv') {
    const slug = domain.replaceAll(/[^a-z0-9]+/gi, '-').toLowerCase();
    return `forecastify-${slug}-${on}.${extension}`;
}
/** See `csvCell`: exhaustive on purpose. */
function textOf(value) {
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return String(value);
    }
    if (value instanceof Date)
        return value.toISOString();
    /* ⚠️ `JSON.stringify` is typed as returning `string`, so `?? ''` is dead code lint refuses — it
     * returns `undefined` only for a function or a symbol, both handled above by falling here from an
     * exhaustive set. A `String()` of either is the wrong answer anyway. */
    return JSON.stringify(value);
}
//# sourceMappingURL=csv.js.map